import { inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { BoTableRow } from 'src/openapi-client';
import { firstValueFrom, throwError } from 'rxjs';
import { ExportService } from '@services/export.service';
import { copyTableToClipboard } from 'src/app/common/util/clipboard';
import { downloadBlob } from 'src/app/common/util/download';
import { NotificationUtils } from 'src/app/common/util/notification';
import {
  CapturedImage,
  captureElementAsPng,
  captureTileAsPng,
  TileCaptureMeta,
  copyPngToClipboard,
  DEFAULT_IMAGE_EXPORT_FORMAT,
  EXPORT_LOGO_URL,
  ImageExportFormat,
  loadImageAsDataUrl,
  nextRender,
  printImage,
} from 'src/app/common/util/image-export';
import { CopyFormat, ExportRequest } from './export-dialog/export.model';

/** What the service needs from an open dialog: close it on success, show an error in it on failure. */
export interface ExportDialogTarget {
  hide(): void;
  showError(message: string): boolean;
}

const SUCCESS_TOAST = { closable: true, durationInMs: 3000 };
const ERROR_TOAST = { closable: true, durationInMs: 5000 };

/**
 * Everything the dashboard does for exporting and copying that is not about the dashboard's own
 * state: capturing it as picture, saving/copying files, calling the export endpoint and telling the
 * user about the result. Provided by `DashboardComponent` (not root), so it lives as long as the dashboard.
 */
@Injectable()
export class DashboardExportActions {
  private readonly transloco = inject(TranslocoService);
  private readonly exportService = inject(ExportService);

  /** True while the dashboard is captured: the template shows the export header (logo, fund, date). */
  readonly capturing = signal(false);
  readonly logo = signal<string | null>(null);

  /** Captures the element as PNG. The export header is shown only for the time of the capture. */
  async captureImage(element: HTMLElement): Promise<CapturedImage> {
    this.logo.set(await loadImageAsDataUrl(EXPORT_LOGO_URL));
    this.capturing.set(true);
    try {
      await nextRender();
      // Charts size themselves on window resize only, so let them adapt to the fixed capture width.
      window.dispatchEvent(new Event('resize'));
      await nextRender();
      return await captureElementAsPng(element);
    } finally {
      this.capturing.set(false);
      window.dispatchEvent(new Event('resize'));
    }
  }

  /** Captures a single tile as PNG, framed by a line with analysis/fund/value mode and one with the Stichtag. */
  async captureTile(element: HTMLElement, meta: TileCaptureMeta): Promise<CapturedImage> {
    // The focus/menu buttons carry data-export-ignore and are left out. Charts are re-laid out first.
    window.dispatchEvent(new Event('resize'));
    await nextRender();
    return captureTileAsPng(element, meta);
  }

  downloadImage(image: CapturedImage | undefined, fileName: string, dialog?: ExportDialogTarget): void {
    try {
      if (!image) {
        throw new Error('Keine Daten zum Exportieren vorhanden');
      }
      downloadBlob(image.blob, `${fileName.replace(/[\\/:*?"<>|]+/g, '-')}.png`);
      dialog?.hide();
      this.success('dashboard.exportDialog.toast.success', { format: DEFAULT_IMAGE_EXPORT_FORMAT });
    } catch (error) {
      this.reportExportError(DEFAULT_IMAGE_EXPORT_FORMAT, error, dialog);
    }
  }

  /**
   * PDF: opens the browser's print dialog ("Save as PDF") directly with the captured picture, there is
   * no own dialog in between. The file name becomes the suggested name of the PDF.
   */
  async printPdf(image: CapturedImage, fileName: string, fitToPage = false): Promise<void> {
    try {
      await printImage(image.previewUrl, fileName.replace(/[\\/:*?"<>|]+/g, '-'), fitToPage ? image : undefined);
      this.success('dashboard.exportDialog.toast.success', { format: 'PDF' satisfies ImageExportFormat });
    } catch (error) {
      this.reportExportError('PDF', error);
    }
  }

  async copyImage(image: CapturedImage | undefined, dialog?: ExportDialogTarget): Promise<void> {
    try {
      if (!image) {
        throw new Error('Keine Daten zum Kopieren vorhanden');
      }
      await copyPngToClipboard(image.blob);
      this.success('dashboard.imageExport.copied');
    } catch (error) {
      this.reportExportError(DEFAULT_IMAGE_EXPORT_FORMAT, error, dialog);
    }
  }

  /** Copies the displayed table (text with tabs + HTML table) into the clipboard. */
  async copyTable(rows: BoTableRow[] | undefined, format: CopyFormat): Promise<void> {
    if (!rows) {
      // The menu entry is disabled in this case, this is only a safeguard.
      NotificationUtils.displayNotification(this.transloco.translate('dashboard.copy.empty'), 'info', ERROR_TOAST);
      return;
    }
    try {
      await copyTableToClipboard(rows);
      this.success('dashboard.copy.toast.success', { format });
    } catch (error) {
      const errorDetail = (error as Error)?.message ?? '';
      NotificationUtils.displayNotification(
        this.transloco.translate('dashboard.copy.error', { format, errorDetail }),
        'error',
        ERROR_TOAST
      );
    }
  }

  /** Lets the backend generate the file (CSV/XLSX) and saves it. Without request there is nothing to export. */
  async exportTile(request: ExportRequest | undefined, dialog?: ExportDialogTarget): Promise<void> {
    const format = request?.format ?? '';
    try {
      await firstValueFrom(
        request
          ? this.exportService.export(request)
          : throwError(() => new Error('Keine exportierbare Kachel vorhanden'))
      );
      dialog?.hide();
      this.success('dashboard.exportDialog.toast.success', { format });
    } catch (error) {
      this.reportExportError(format, error, dialog);
    }
  }

  /** Shows the error in the open dialog, or as toast if there is none. */
  reportExportError(format: string, error: unknown, dialog?: ExportDialogTarget): void {
    const message = this.transloco.translate('dashboard.exportDialog.exportError', {
      format,
      errorDetail: (error as Error)?.message ?? '',
    });
    if (!dialog?.showError(message)) {
      NotificationUtils.displayNotification(message, 'error', ERROR_TOAST);
    }
  }

  private success(key: string, params?: Record<string, unknown>): void {
    NotificationUtils.displayNotification(this.transloco.translate(key, params), 'success', SUCCESS_TOAST);
  }
}
